import { OmniScrollArea } from "@/components/artifacts/OmniScrollArea.tsx";
import { connectorQueries } from "@/components/connectors/api.ts";
import { ConnectorConnectAction } from "@/components/connectors/ConnectorConnectAction.tsx";
import { ConnectorLogo } from "@/components/connectors/ConnectorLogo.tsx";
import { getConnectorIconForVariant } from "@/components/connectors/providerIcons.tsx";
import { useConnectorCatalog } from "@/components/connectors/useConnectorCatalog.ts";
import { Input } from "@/components/ui/input.tsx";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import { cn } from "@/lib/utils.ts";
import { connectorConnectMode } from "@/shared/connectors/connectMode.ts";
import {
  type ConnectorPickerFraming,
  connectorAccountsByCatalogId,
  connectorDisplayTitle,
  connectorPickerSections,
  connectorThreeSections,
  matchesConnectedEntrySearch,
  matchesConnectorSearch,
} from "@/shared/connectors/pickerSections.ts";
import type { ConnectorCatalogItem, ConnectorSummary } from "@/types/connectors.ts";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";

/** Show the search box only once the list is long enough to need one. */
const SEARCH_THRESHOLD = 6;

/**
 * The connector gallery, sized for the omnibox card.
 *
 * Same catalog and the same framing as the canvas's `ConnectorPickerBlock`, in
 * the guided flow's shape rather than the canvas's: search at the top, then one
 * list — recommended first, everything else under it. The canvas has a screen
 * height to spend and can afford a grid of description-carrying tiles; here the
 * card floats over the thread above a "Done" row and a follow-up box, so the
 * rows are dense and the list is capped and scrolls rather than pushing the
 * composer off the top of the viewport.
 *
 * A row is not a toggle — it is a handoff to the provider. That is survivable
 * here for two reasons that are worth naming, because neither is this
 * component's doing and both would be easy to break. The connect opens in a tab
 * of its own (`ConnectorConnectAction` renders a `target="_blank"` link), so
 * this card is never unmounted mid-flow and the user comes back to the same
 * omnibox they left. And `useConnectorCatalog` bundles the catalog query with
 * the `useConnectorOAuthResults` subscription, so the tab that finishes the
 * OAuth reports back over a BroadcastChannel and the row flips to "Connected"
 * in place — no refetch-on-focus, no reload, and nothing for the user to do but
 * see that it worked (BIG-7231).
 *
 * Connects commit as they are made, so "Done" ends the step rather than
 * committing anything, and a user who dismisses the card keeps what they
 * already connected.
 */
export const ConnectorsOmniPickerBody = ({
  scopeIds,
  pinnedIds,
  preferredIds,
}: ConnectorPickerFraming) => {
  const [search, setSearch] = useState("");
  // Toasts off: a thread can hold more than one of these cards, and the canvas
  // card can be open behind this one — each would otherwise announce the same
  // connection.
  const { data: catalog, isLoading, isError } = useConnectorCatalog({ showToasts: false });

  const { pinned, rest } = useMemo(
    () => connectorPickerSections(catalog?.items ?? [], { scopeIds, pinnedIds, preferredIds }),
    [catalog, scopeIds, pinnedIds, preferredIds],
  );

  // Each connected account gets a row of its own, labeled by its email — the
  // same per-account model the connectors gallery uses — so a service row
  // never says "Connected" and offers "Connect" in the same breath. The
  // grouping and the three-section split are shared with the canvas card, so
  // the two pickers cannot drift. This query gates rendering exactly like the
  // catalog's: without it a connected account has no row, and the picker would
  // read as "nothing connected" while the data is merely in flight.
  const {
    data: accountsData,
    isLoading: accountsLoading,
    isError: accountsError,
  } = useQuery(connectorQueries.list());
  const sections = useMemo(
    () =>
      connectorThreeSections(
        pinned,
        rest,
        connectorAccountsByCatalogId(accountsData?.connectors ?? []),
      ),
    [pinned, rest, accountsData],
  );

  const total =
    sections.recommended.length + sections.connected.length + sections.additional.length;

  // Skeleton rows rather than a spinner: they hold the height the list is about
  // to take, so the card doesn't grow under the cursor as the catalog lands. (A
  // spinner here would also drag the lottie player into every biscuit's import
  // graph — this body is reached from the shared card chrome.)
  if (isLoading || accountsLoading) {
    return (
      <div className="flex flex-col gap-2 rounded-md border p-3">
        <Skeleton className="h-3.5 w-32" />
        <Skeleton className="h-3.5 w-44" />
        <Skeleton className="h-3.5 w-36" />
      </div>
    );
  }

  // A failed catalog read leaves no data and is no longer loading, which is
  // indistinguishable from an empty catalog unless it says so — and the two want
  // opposite things from the user. "Nothing to connect" is an answer and ends the
  // step; an outage is a reason to come back. Same shape as the guided flow's
  // service picker, which draws the same distinction.
  if (isError || accountsError) {
    return (
      <p className="text-xs text-muted-foreground">
        Couldn't load services just now. Try again in a moment.
      </p>
    );
  }

  if (total === 0) {
    return <p className="text-xs text-muted-foreground">No services are available to connect.</p>;
  }

  // Built below the guards: on a loading or failed card every row here would be
  // constructed and thrown away. Display order with the empty sections dropped,
  // so each band that renders owns exactly one sticky heading.
  const bands = [
    {
      label: "Recommended",
      rows: sections.recommended
        .filter((item) => matchesConnectorSearch(item, search))
        .map((item) => <ConnectorRow key={item.id} item={item} stateShownElsewhere />),
    },
    {
      label: "Connected",
      rows: sections.connected
        .filter((entry) => matchesConnectedEntrySearch(entry, search))
        .map(({ item, connector }) =>
          connector ? (
            <ConnectorAccountRow
              key={`account:${connector.id}`}
              item={item}
              connector={connector}
            />
          ) : (
            <ConnectorRow key={item.id} item={item} />
          ),
        ),
    },
    {
      label: "Additional Connectors",
      rows: sections.additional
        .filter((item) => matchesConnectorSearch(item, search))
        .map((item) => <ConnectorRow key={item.id} item={item} stateShownElsewhere />),
    },
  ].filter((band) => band.rows.length > 0);

  // Headings only when there is more than one section to tell apart — one alone
  // would label a list against nothing, which reads as a rendering bug.
  const showHeadings = bands.length >= 2;

  return (
    // gap-4 matches the card's own spacing above the search field, so the
    // header, search, and table read as evenly separated blocks.
    <div className="flex flex-col gap-4">
      {/* Its own field above the table rather than a row inside it, so the
          list scrolls beneath a field that never moves — and the table's top
          cell is its first section heading. */}
      {total >= SEARCH_THRESHOLD && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search connectors"
            className="h-9 bg-white pl-8 text-sm"
          />
        </div>
      )}

      {/* The border rides a wrapper rather than the scrolling box, as it does on
          the other pickers: under border-box sizing a border on the capped
          element is spent out of the cap, which is enough to slice the last row.

          The cap holds whole rows under a heading: a 29px band plus three 36px
          rows on a phone, where this card shares the screen with the keyboard,
          and plus five on a desktop. Deep enough that a scoped onboarding card
          shows everything without moving, shallow enough that the full catalog
          can't take the whole thread with it. A lone section draws no heading,
          so it spends that 29px on part of a fourth row. */}
      <div className="overflow-hidden rounded-md border">
        <OmniScrollArea className="max-h-[137px] md:max-h-[209px]">
          {bands.length === 0 ? (
            <p className="px-3 py-4 text-xs text-muted-foreground">
              No services match your search.
            </p>
          ) : (
            bands.map((band) => (
              <div key={band.label}>
                {showHeadings && <SectionHeading>{band.label}</SectionHeading>}
                {band.rows}
              </div>
            ))
          )}
        </OmniScrollArea>
      </div>
    </div>
  );
};

/**
 * One service row: brand mark (favicon fallback), name, status, and the connect
 * action.
 *
 * A connected, healthy connector keeps its row and says so. `ConnectorTile` on
 * the canvas does the same, and the reason matters more here than there: this
 * card is answering "which of these do you want?", and a service dropping out of
 * the list the moment it connects reads as the pick being lost rather than
 * taken. `ConnectorConnectAction` renders nothing when there is nothing left to
 * offer, so the row simply loses its button.
 */
const ConnectorRow = ({
  item,
  stateShownElsewhere,
}: {
  item: ConnectorCatalogItem;
  /** Set on rows in the connect sections: the Connected section owns the
   *  item's state, so this row only offers the action. */
  stateShownElsewhere?: boolean;
}) => {
  const { needsReconnect } = connectorConnectMode(item);
  const brandIcon = item.provider
    ? getConnectorIconForVariant(item.provider, item.iconVariant ?? "brand")
    : null;

  return (
    <div className="flex items-center gap-2.5 px-3 py-2">
      <span className="flex size-5 shrink-0 items-center justify-center">
        {brandIcon ?? <ConnectorLogo websiteUrl={item.websiteUrl} name={item.name} size="sm" />}
      </span>
      <span className="min-w-0 flex-1 truncate text-sm">{connectorDisplayTitle(item)}</span>
      {/* "Reconnect" is the button's own label, so saying it here too would put
          the word on the row twice. */}
      {item.connected && !needsReconnect && !stateShownElsewhere && (
        <StatusLabel>Connected</StatusLabel>
      )}
      <ConnectorConnectAction item={item} statsigName="omni-picker-connector-connect" />
    </div>
  );
};

/**
 * One connected account: the service's mark and name with the account's email
 * under it, and the connection's state on the right. Mirrors the gallery's
 * per-account cards, where each of two Google accounts is its own thing —
 * status only, the actions (sync, remove) stay on the gallery.
 */
const ConnectorAccountRow = ({
  item,
  connector,
}: {
  item: ConnectorCatalogItem;
  connector: ConnectorSummary;
}) => {
  const brandIcon = item.provider
    ? getConnectorIconForVariant(item.provider, item.iconVariant ?? "brand")
    : null;

  return (
    <div className="flex items-center gap-2.5 px-3 py-2">
      <span className="flex size-5 shrink-0 items-center justify-center">
        {brandIcon ?? <ConnectorLogo websiteUrl={item.websiteUrl} name={item.name} size="sm" />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm">{connectorDisplayTitle(item)}</span>
        {/* The identity line: which account this row is. Falls back to the
            connector's display name when the provider carries no email. */}
        <span className="block truncate text-xs text-muted-foreground">
          {connector.email ?? connector.displayName}
        </span>
      </span>
      {connector.needsAuth ? (
        <StatusLabel tone="alert">Reconnect needed</StatusLabel>
      ) : (
        <StatusLabel>Connected</StatusLabel>
      )}
    </div>
  );
};

/**
 * A status label wearing the connect button's box metrics — h-6, px-2, and an
 * invisible border — so label text and button text right-align down the
 * table instead of the bare text sitting further right than the pill's.
 */
const StatusLabel = ({ tone, children }: { tone?: "alert"; children: ReactNode }) => (
  <span
    className={cn(
      "flex h-6 shrink-0 items-center border border-transparent px-2 text-xs",
      // Amber, not red: this asks the user to reconnect, it does not report
      // a failure. The same state wears the same colour on iOS.
      tone === "alert" ? "font-medium text-amber-700" : "text-muted-foreground",
    )}
  >
    {children}
  </span>
);

const SectionHeading = ({ children }: { children: ReactNode }) => (
  // Sticky, and in the scroll flow: that is what makes a section's band hold the
  // top while its own rows pass under it and then get pushed off by the next
  // section's. Solid rather than tinted, because a translucent band shows every
  // row passing behind it.
  <div className="sticky top-0 z-10 border-b bg-muted px-3 py-1.5 text-xs font-medium text-muted-foreground">
    {children}
  </div>
);
